import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Phone, Mail, MapPin, Clock, ArrowRight, ChevronRight,
  Box, Palette, Printer, BookOpen, Package, Globe, Gift,
  CheckCircle, Star, Award, Zap, Users, Send,
 MessageCircle,
  ChevronDown, ExternalLink, X,
} from "lucide-react";
import { LinkedInLogoIcon, TwitterLogoIcon, InstagramLogoIcon, FaceIcon} from "@radix-ui/react-icons";
// import { Facebook } from "@mui/icons-material";

// ─── Design Tokens ─────────────────────────────────────────────
const R   = "#E31E24";
const RDK = "#C01019";
const BG  = "#0A0A0A";
const S1  = "#141414";
const S2  = "#1A1A1A";
const BD  = "rgba(255,255,255,0.07)";
const MT  = "rgba(255,255,255,0.52)";
const MTL = "rgba(255,255,255,0.75)";

// ─── useInView ─────────────────────────────────────────────────
function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
}

// ─── Global CSS ────────────────────────────────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=Playfair+Display:ital,wght@1,400;1,700&display=swap');

*,*::before,*::after { box-sizing: border-box; margin: 0; padding: 0; }
html { scroll-behavior: smooth; }
body { background: #0A0A0A; color: #fff; font-family: 'Inter', sans-serif; -webkit-font-smoothing: antialiased; overflow-x: hidden; }
a { text-decoration: none; color: inherit; }
button { font-family: 'Inter', sans-serif; cursor: pointer; }
img { display: block; max-width: 100%; }
textarea { resize: vertical; }

/* ── Animations ── */
@keyframes ct-float  { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
@keyframes ct-pulse  { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: .4; transform: scale(1.6); } }
@keyframes ct-glow   { 0%,100% { opacity: .55; } 50% { opacity: 1; } }
@keyframes ct-spin   { to { transform: rotate(360deg); } }

/* ── Scroll reveal ── */
.ct-fade  { opacity: 0; transform: translateY(24px);  transition: opacity .68s cubic-bezier(.22,1,.36,1), transform .68s cubic-bezier(.22,1,.36,1); }
.ct-scl   { opacity: 0; transform: scale(.9);          transition: opacity .6s  cubic-bezier(.22,1,.36,1), transform .6s  cubic-bezier(.22,1,.36,1); }
.ct-left  { opacity: 0; transform: translateX(-26px);  transition: opacity .7s  cubic-bezier(.22,1,.36,1), transform .7s  cubic-bezier(.22,1,.36,1); }
.ct-right { opacity: 0; transform: translateX(26px);   transition: opacity .7s  cubic-bezier(.22,1,.36,1), transform .7s  cubic-bezier(.22,1,.36,1); }
.ct-fade.in, .ct-scl.in, .ct-left.in, .ct-right.in { opacity: 1; transform: none; }
.d1{transition-delay:.07s}.d2{transition-delay:.14s}.d3{transition-delay:.21s}
.d4{transition-delay:.28s}.d5{transition-delay:.35s}.d6{transition-delay:.42s}

/* ── Buttons ── */
.btn-r {
  background: #E31E24; color: #fff; border: none;
  padding: 12px 26px; border-radius: 8px; font-size: 14px; font-weight: 700;
  display: inline-flex; align-items: center; gap: 8px;
  transition: background .2s, transform .15s, box-shadow .2s; white-space: nowrap;
}
.btn-r:hover { background: #C01019; transform: translateY(-2px); box-shadow: 0 10px 30px rgba(227,30,36,.42); }
.btn-r.lg   { font-size: 15.5px; padding: 14px 32px; border-radius: 9px; }
.btn-r.full { width: 100%; justify-content: center; padding: 14px 24px; font-size: 15px; }
.btn-r.sm   { font-size: 12px; padding: 8px 16px; border-radius: 6px; }

.btn-g {
  background: rgba(255,255,255,.06); color: #fff; border: 1px solid rgba(255,255,255,.13);
  padding: 12px 26px; border-radius: 8px; font-size: 14px; font-weight: 600;
  display: inline-flex; align-items: center; gap: 8px;
  transition: background .2s, border-color .2s, transform .15s; white-space: nowrap;
}
.btn-g:hover { background: rgba(227,30,36,.1); border-color: rgba(227,30,36,.32); transform: translateY(-2px); }
.btn-g.lg { font-size: 15.5px; padding: 14px 32px; border-radius: 9px; }
.btn-g.sm { font-size: 12px; padding: 8px 16px; border-radius: 6px; }

/* ── Pill label ── */
.pill { display: inline-flex; align-items: center; gap: 8px; background: rgba(227,30,36,.11); border: 1px solid rgba(227,30,36,.28); color: #E31E24; font-size: 10.5px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; padding: 5px 14px; border-radius: 100px; }
.dot  { width: 5px; height: 5px; border-radius: 50%; background: #E31E24; animation: ct-pulse 2s infinite; flex-shrink: 0; }

/* ── Section shell ── */
.sec        { padding: clamp(56px,8vh,100px) clamp(16px,5vw,80px); }
.sec-inner  { max-width: 1240px; margin: 0 auto; }
.gd         { height: 1px; background: linear-gradient(90deg, transparent, rgba(227,30,36,.42), transparent); }
.sec-h2     { font-size: clamp(24px,3.5vw,48px); font-weight: 900; line-height: 1.06; letter-spacing: -1.2px; color: #fff; }
.sec-h2 em  { font-family: 'Playfair Display', serif; font-style: italic; color: #E31E24; }

/* ── Bento grid ── */
.bento { display: grid; grid-template-columns: repeat(12,1fr); gap: 10px; }
.bento.rauto { grid-auto-rows: auto; }
.c3{grid-column:span 3}.c4{grid-column:span 4}.c5{grid-column:span 5}
.c6{grid-column:span 6}.c7{grid-column:span 7}.c8{grid-column:span 8}
.c9{grid-column:span 9}.c12{grid-column:span 12}
.r2{grid-row:span 2}.r3{grid-row:span 3}

/* ── Card ── */
.card {
  background: #141414; border: 1px solid rgba(255,255,255,.07); border-radius: 18px;
  overflow: hidden; position: relative;
  transition: transform .32s cubic-bezier(.22,1,.36,1), border-color .28s, box-shadow .28s;
}
.card:hover { transform: translateY(-4px) scale(1.007); border-color: rgba(227,30,36,.26); box-shadow: 0 20px 52px rgba(0,0,0,.65); }
.card-body  { padding: clamp(18px,2.2vw,28px); }

/* ── Icon box ── */
.ibox { width: 44px; height: 44px; border-radius: 10px; background: rgba(227,30,36,.14); border: 1px solid rgba(227,30,36,.26); display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: transform .28s; }
.ibox.lg { width: 52px; height: 52px; border-radius: 12px; }
.card:hover .ibox { transform: scale(1.1); }

/* ── Form ── */
.form-wrap { background: #141414; border: 1px solid rgba(255,255,255,.07); border-radius: 20px; padding: clamp(24px,3vw,44px); }
.form-group { display: flex; flex-direction: column; gap: 6px; }
.form-label { font-size: 12.5px; font-weight: 600; color: rgba(255,255,255,.72); letter-spacing: .3px; }
.form-input {
  background: rgba(255,255,255,.05); border: 1px solid rgba(255,255,255,.1); border-radius: 9px;
  padding: 12px 16px; font-size: 14px; color: #fff; font-family: 'Inter', sans-serif;
  outline: none; transition: border-color .2s, background .2s; width: 100%;
}
.form-input:focus  { border-color: rgba(227,30,36,.45); background: rgba(227,30,36,.04); }
.form-input.err    { border-color: rgba(227,30,36,.65); background: rgba(227,30,36,.05); }
.form-input::placeholder { color: rgba(255,255,255,.28); }
.form-select {
  appearance: none;
  background: rgba(255,255,255,.05) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='rgba(255,255,255,0.35)' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E") no-repeat right 14px center;
  cursor: pointer;
}
.form-select option { background: #1a1a1a; color: #fff; }
.err-msg { font-size: 11.5px; color: #E31E24; font-weight: 500; }
.form-row  { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.check-lbl { display: flex; align-items: flex-start; gap: 10px; cursor: pointer; font-size: 13.5px; color: rgba(255,255,255,.72); line-height: 1.4; }
.check-inp { width: 18px; height: 18px; border-radius: 4px; border: 1.5px solid rgba(255,255,255,.25); background: rgba(255,255,255,.05); cursor: pointer; flex-shrink: 0; margin-top: 1px; accent-color: #E31E24; }

/* ── FAQ ── */
.faq-item  { border-bottom: 1px solid rgba(255,255,255,.07); }
.faq-btn   { width: 100%; background: none; border: none; color: #fff; display: flex; justify-content: space-between; align-items: center; padding: 20px 0; font-size: clamp(14.5px,1.4vw,16.5px); font-weight: 600; text-align: left; cursor: pointer; gap: 16px; transition: color .2s; }
.faq-btn:hover { color: #E31E24; }
.faq-icon  { flex-shrink: 0; transition: transform .32s cubic-bezier(.22,1,.36,1), color .2s; color: rgba(255,255,255,.38); }
.faq-icon.open { transform: rotate(180deg); color: #E31E24; }
.faq-body  { overflow: hidden; transition: max-height .4s cubic-bezier(.22,1,.36,1), opacity .3s; }
.faq-body.closed { max-height: 0 !important; opacity: 0; }
.faq-body.open   { opacity: 1; }
.faq-text  { padding: 0 0 18px; font-size: 14px; color: rgba(255,255,255,.54); line-height: 1.75; }

/* ── Stats ── */
.stats-bar { display: grid; grid-template-columns: repeat(4,1fr); }
.stat-item { padding: clamp(22px,3vw,38px) clamp(14px,2.5vw,28px); text-align: center; position: relative; }
.stat-item+.stat-item::before { content:''; position: absolute; left:0; top:22%; bottom:22%; width:1px; background: rgba(255,255,255,.08); }
.stat-n   { font-size: clamp(30px,4.2vw,54px); font-weight: 900; line-height: 1; letter-spacing: -2px; color: #fff; }
.stat-n span { color: #E31E24; }
.stat-l   { font-size: 12.5px; color: rgba(255,255,255,.42); margin-top: 5px; font-weight: 500; }

/* ── Hours ── */
.h-row { display: flex; justify-content: space-between; align-items: center; padding: 9px 0; border-bottom: 1px solid rgba(255,255,255,.06); font-size: 13px; }
.h-row:last-child { border-bottom: none; }
.h-day  { color: rgba(255,255,255,.62); font-weight: 500; }
.h-time { color: #fff; font-weight: 600; }
.h-closed { color: rgba(227,30,36,.8); font-weight: 600; }

/* ── Social ── */
.soc-btn {
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px;
  padding: 24px 12px; border-radius: 16px; border: 1px solid rgba(255,255,255,.07);
  background: rgba(255,255,255,.03); cursor: pointer;
  transition: background .25s, border-color .25s, transform .25s;
  text-decoration: none;
}
.soc-btn:hover { transform: translateY(-3px); }
.soc-icon-wrap { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; }
.soc-label { font-size: 12px; font-weight: 600; color: rgba(255,255,255,.58); letter-spacing: .3px; }

/* ── Map ── */
.map-shell { border-radius: 18px; overflow: hidden; border: 1px solid rgba(255,255,255,.07); position: relative; }
.map-overlay { position: absolute; top: 16px; left: 16px; z-index: 1; background: rgba(10,10,10,.88); backdrop-filter: blur(14px); border: 1px solid rgba(255,255,255,.1); border-radius: 13px; padding: 14px 18px; min-width: 200px; }

/* ── CTA ── */
.cta-wrap { border-radius: 22px; overflow: hidden; position: relative; padding: clamp(52px,7vw,88px) clamp(28px,5vw,72px); text-align: center; background: linear-gradient(135deg,#1a0404 0%,#1c1c1c 50%,#1a0404 100%); border: 1px solid rgba(227,30,36,.2); }
.cta-orb  { position: absolute; top:50%; left:50%; transform: translate(-50%,-50%); width:500px; height:300px; background: radial-gradient(ellipse,rgba(227,30,36,.11) 0%,transparent 65%); pointer-events:none; }

/* ── Success state ── */
.success-wrap { display: flex; flex-direction: column; align-items: center; gap: 16px; padding: 52px 24px; text-align: center; }
.success-ring { width: 68px; height: 68px; border-radius: 50%; background: rgba(227,30,36,.12); border: 2px solid rgba(227,30,36,.32); display: flex; align-items: center; justify-content: center; animation: ct-glow 2.5s ease-in-out infinite; }

/* ── Footer ── */
.footer { background: #0A0A0A; border-top: 1px solid rgba(255,255,255,.07); padding: clamp(44px,6vh,72px) clamp(16px,5vw,80px) 24px; }
.footer-grid { max-width: 1240px; margin: 0 auto; display: grid; grid-template-columns: 2fr 1fr 1fr 1.4fr; gap: clamp(20px,4vw,52px); margin-bottom: 44px; }
.footer-h    { font-size: 11.5px; font-weight: 700; color: rgba(255,255,255,.82); letter-spacing: 1.8px; text-transform: uppercase; margin-bottom: 16px; }
.footer-lnk  { display: block; font-size: 13px; color: rgba(255,255,255,.42); margin-bottom: 9px; transition: color .2s; }
.footer-lnk:hover { color: #fff; }
.f-soc       { display: flex; gap: 9px; margin-top: 18px; }
.f-soc-btn   { width: 34px; height: 34px; border-radius: 7px; background: rgba(255,255,255,.05); border: 1px solid rgba(255,255,255,.09); display: flex; align-items: center; justify-content: center; color: rgba(255,255,255,.45); transition: background .2s, color .2s, border-color .2s; cursor: pointer; }
.f-soc-btn:hover { background: rgba(227,30,36,.13); color: #E31E24; border-color: rgba(227,30,36,.28); }
.nl-form  { display: flex; gap: 7px; margin-top: 12px; }
.nl-input { flex: 1; background: rgba(255,255,255,.05); border: 1px solid rgba(255,255,255,.1); border-radius: 7px; padding: 9px 13px; font-size: 13px; color: #fff; font-family: 'Inter', sans-serif; outline: none; transition: border-color .2s; }
.nl-input:focus { border-color: rgba(227,30,36,.4); }
.nl-input::placeholder { color: rgba(255,255,255,.28); }
.footer-bot { max-width:1240px; margin:0 auto; display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:10px; padding-top:20px; border-top:1px solid rgba(255,255,255,.07); }

/* ── Responsive ── */
@media(max-width:1024px){
  .bento { grid-template-columns: repeat(6,1fr); }
  .c3,.c4 { grid-column: span 3; }
  .c5,.c6 { grid-column: span 3; }
  .c7,.c8,.c9,.c12 { grid-column: span 6; }
  .ct-form-grid { grid-template-columns: 1fr !important; }
  .ct-faq-grid  { grid-template-columns: 1fr !important; }
  .stats-bar { grid-template-columns: repeat(2,1fr); }
  .stat-item:nth-child(3)::before { display: none; }
  .footer-grid { grid-template-columns: 1fr 1fr; }
  .svc-bento { grid-template-columns: repeat(2,1fr) !important; }
}
@media(max-width:640px){
  .bento { grid-template-columns: repeat(2,1fr); }
  .c3,.c4,.c5,.c6,.c7,.c8,.c9,.c12 { grid-column: span 2; }
  .form-row { grid-template-columns: 1fr; }
  .stats-bar { grid-template-columns: 1fr 1fr; }
  .footer-grid { grid-template-columns: 1fr; }
  .svc-bento { grid-template-columns: 1fr !important; }
  .soc-grid  { grid-template-columns: repeat(3,1fr) !important; }
}
:focus-visible { outline: 2px solid #E31E24; outline-offset: 3px; border-radius: 4px; }
@media(prefers-reduced-motion:reduce) { * { animation-duration:.01ms!important; transition-duration:.01ms!important; } }
`;

// ─── Data ──────────────────────────────────────────────────────
const SERVICES_OPTS = [
  "3D Signage", "Branding & Identity", "Printing Services",
  "Book Printing", "Packaging Solutions", "Web Design",
  "Promotional Merchandise", "Large Format Printing",
];

const BUDGETS = [
  "Under KES 10,000", "KES 10,000 – 50,000",
  "KES 50,000 – 150,000", "KES 150,000 – 500,000", "Over KES 500,000",
];

const FAQS = [
  { q:"How long does a project take?",          a:"Timelines vary by project type. Business cards and flyers: 2–3 days. 3D signage: 1–2 weeks. Full rebrands and websites: 2–6 weeks. We'll give you a precise timeline upfront." },
  { q:"Do you offer nationwide delivery?",       a:"Yes. We deliver across Kenya. Nairobi clients receive same or next-day delivery for most print jobs. Upcountry delivery is handled via courier — we'll recommend the best option for your project." },
  { q:"Can I request a custom quotation?",       a:"Absolutely. Fill in the contact form or call us directly. We provide free, detailed quotations within 24 hours — no obligation." },
  { q:"Do you handle urgent printing projects?", a:"Yes. We offer express turnaround for urgent projects. Additional charges may apply based on quantity and complexity. Call 0794 437 498 to discuss your deadline." },
  { q:"Do you offer website maintenance?",       a:"Yes. We offer monthly maintenance packages covering security updates, content changes, backups and performance monitoring. Ask us about plans when you enquire." },
  { q:"Can you design and print books?",         a:"Yes — book printing is a core service. We handle typesetting, cover design, printing and perfect binding end to end. Minimum quantities apply." },
];

const QUICK_SVCS = [
  { Icon:Box,      label:"3D Signage",           desc:"Shop fronts, reception signs, illuminated channel letters." },
  { Icon:Printer,  label:"Printing Services",     desc:"Business cards, flyers, brochures and posters." },
  { Icon:BookOpen, label:"Book Printing",         desc:"Magazines, hymnals, catalogues and perfect binding." },
  { Icon:Package,  label:"Packaging Solutions",   desc:"Branded bags, boxes, labels and gift packaging." },
  { Icon:Globe,    label:"Web Design",            desc:"Fast, modern websites built to convert visitors." },
  { Icon:Gift,     label:"Promotional Merch",     desc:"Branded mugs, t-shirts, caps and corporate gifts." },
];

const SOCIALS = [
  { Icon:FaceIcon,       label:"Facebook",  color:"#1877F2", bg:"rgba(24,119,242,.15)", href:"#" },
  { Icon:InstagramLogoIcon,      label:"Instagram", color:"#E1306C", bg:"rgba(225,48,108,.15)", href:"#" },
  { Icon:LinkedInLogoIcon,       label:"LinkedIn",  color:"#0A66C2", bg:"rgba(10,102,194,.15)", href:"#" },
  { Icon:MessageCircle,  label:"WhatsApp",  color:"#25D366", bg:"rgba(37,211,102,.15)", href:"https://wa.me/254794437498" },
  
];

const FOOTER_SVCS  = ["3D Signage","Brand Identity","Printing","Book Printing","Packaging","Web Design","Promo Merch","Large Format"];
const FOOTER_LINKS = ["About Us","Portfolio","Our Process","Testimonials","Blog","Careers","Privacy Policy","Terms"];

// ─── Shared UI ─────────────────────────────────────────────────
function Pill({ children }) {
  return <div className="pill"><span className="dot" />{children}</div>;
}
function GD() { return <div className="gd" />; }

// ─── HERO ──────────────────────────────────────────────────────
function Hero() {
  const [ref, vis] = useInView(0.05);
  const navigate   = useNavigate();
  return (
    <section
      ref={ref}
      aria-label="Contact hero"
      style={{
        minHeight: "78vh",
        background: `radial-gradient(ellipse 70% 55% at 55% 35%,rgba(227,30,36,.1) 0%,transparent 68%),
                     radial-gradient(ellipse 40% 30% at 8% 80%,rgba(227,30,36,.05) 0%,transparent 60%),${BG}`,
        position: "relative", overflow: "hidden",
        display: "flex", alignItems: "center",
        padding: "clamp(96px,13vh,152px) clamp(16px,5vw,80px) clamp(56px,7vh,88px)",
      }}
    >
      {/* Grid texture */}
      <div style={{ position:"absolute",inset:0,backgroundImage:`linear-gradient(rgba(227,30,36,.035) 1px,transparent 1px),linear-gradient(90deg,rgba(227,30,36,.035) 1px,transparent 1px)`,backgroundSize:"56px 56px",pointerEvents:"none" }} />
      {/* Floating shapes */}
      <div style={{ position:"absolute",top:"17%",right:"7%",width:52,height:52,border:"1.5px solid rgba(227,30,36,.22)",borderRadius:11,transform:"rotate(22deg)",animation:"ct-float 5.5s ease-in-out infinite",pointerEvents:"none" }} />
      <div style={{ position:"absolute",bottom:"22%",right:"19%",width:30,height:30,background:"rgba(227,30,36,.12)",borderRadius:"50%",animation:"ct-float 7s ease-in-out infinite",pointerEvents:"none" }} />
      <div style={{ position:"absolute",top:"60%",left:"3%",width:16,height:16,background:R,borderRadius:"50%",opacity:.26,animation:"ct-float 6s ease-in-out infinite 1.5s",pointerEvents:"none" }} />

      <div style={{ maxWidth:1240,margin:"0 auto",width:"100%",position:"relative",zIndex:1 }}>
        <div className={`ct-fade${vis?" in":""}`} style={{ marginBottom:18 }}><Pill>Contact Us</Pill></div>
        <h1
          className={`ct-fade d1${vis?" in":""}`}
          style={{ fontSize:"clamp(36px,5.2vw,72px)",fontWeight:900,lineHeight:1.03,letterSpacing:-2.5,color:"#fff",maxWidth:800,marginBottom:20 }}
        >
          Let's Build Something{" "}
          <span style={{ fontFamily:"'Playfair Display',serif",fontStyle:"italic",color:R,filter:"drop-shadow(0 0 28px rgba(227,30,36,.5))" }}>
            Amazing
          </span>{" "}
          Together
        </h1>
        <p className={`ct-fade d2${vis?" in":""}`} style={{ fontSize:"clamp(15px,1.5vw,17.5px)",color:MT,lineHeight:1.76,maxWidth:560,marginBottom:40 }}>
          Whether you need branding, printing, signage, packaging, book printing, promotional merchandise, or web design, we're ready to bring your vision to life.
        </p>
        <div className={`ct-fade d3${vis?" in":""}`} style={{ display:"flex",gap:14,flexWrap:"wrap" }}>
          <button className="ft-btn-r ft-btn--lg" onClick={() => navigate("/contact")}>
            Get Free Quote <ArrowRight size={16} />
          </button>
          <a href="tel:0794437498" className="btn-g lg"><Phone size={15} /> Call Us Now</a>
        </div>
      </div>
    </section>
  );
}

// ─── CONTACT INFO BENTO ────────────────────────────────────────
function ContactInfo() {
  const [ref, vis] = useInView(0.08);
  return (
    <section className="sec" ref={ref} aria-label="Contact information" style={{ background:S1,borderTop:`1px solid ${BD}` }}>
      <div className="sec-inner">
        <div style={{ textAlign:"center",marginBottom:44 }}>
          <div className={`ct-fade${vis?" in":""}`} style={{ display:"flex",justifyContent:"center",marginBottom:14 }}><Pill>Get In Touch</Pill></div>
          <h2 className={`ct-fade d1${vis?" in":""} sec-h2`} style={{ textAlign:"center" }}>Multiple Ways to <em>Reach Us</em></h2>
        </div>

        <div className="bento rauto">
          {/* Phone — c5, large accent card */}
          <div className={`card ct-scl d1${vis?" in":""} c5`} style={{ background:"linear-gradient(135deg,#1e0404 0%,#1a1a1a 100%)",borderColor:"rgba(227,30,36,.22)" }}>
            <div style={{ position:"absolute",inset:0,background:"radial-gradient(circle at 80% 20%,rgba(227,30,36,.12) 0%,transparent 55%)",pointerEvents:"none" }} />
            <div className="card-body" style={{ position:"relative",zIndex:1,display:"flex",flexDirection:"column",gap:18 }}>
              <div className="ibox lg"><Phone size={22} color={R} strokeWidth={1.6} /></div>
              <div>
                <div style={{ fontSize:11,fontWeight:700,letterSpacing:"1.8px",textTransform:"uppercase",color:R,marginBottom:6 }}>Phone</div>
                <div style={{ fontSize:"clamp(20px,2.5vw,30px)",fontWeight:900,color:"#fff",letterSpacing:-.5,marginBottom:8 }}>0794 437 498</div>
                <p style={{ fontSize:13.5,color:MT,lineHeight:1.65 }}>Available Mon–Sat. Call or WhatsApp us for a fast response.</p>
              </div>
              <div style={{ display:"flex",gap:10,flexWrap:"wrap" }}>
                <a href="tel:0794437498" className="btn-r" style={{ fontSize:13,padding:"10px 20px" }}><Phone size={13} /> Call Now</a>
                <a href="https://wa.me/254794437498" className="btn-g" style={{ fontSize:13,padding:"10px 20px" }}>WhatsApp <ChevronRight size={13} /></a>
              </div>
            </div>
          </div>

          {/* Email — c5 */}
          <div className={`card ct-scl d2${vis?" in":""} c5`}>
            <div className="card-body" style={{ display:"flex",flexDirection:"column",gap:18 }}>
              <div className="ibox lg"><Mail size={22} color={R} strokeWidth={1.6} /></div>
              <div>
                <div style={{ fontSize:11,fontWeight:700,letterSpacing:"1.8px",textTransform:"uppercase",color:R,marginBottom:6 }}>Email</div>
                <div style={{ display:"flex",flexDirection:"column",gap:5,marginBottom:8 }}>
                  {["info@bestmarkdesigner.com","bestmarkdesigner@gmail.com"].map(em => (
                    <a key={em} href={`mailto:${em}`} style={{ fontSize:"clamp(13px,1.3vw,15px)",fontWeight:700,color:"#fff",transition:"color .2s" }}
                      onMouseEnter={e=>e.currentTarget.style.color=R}
                      onMouseLeave={e=>e.currentTarget.style.color="#fff"}
                    >{em}</a>
                  ))}
                </div>
                <p style={{ fontSize:13.5,color:MT,lineHeight:1.65 }}>We respond to all emails within 24 hours, every working day.</p>
              </div>
              <a href="mailto:info@bestmarkdesigner.com" className="btn-r" style={{ alignSelf:"flex-start",fontSize:13,padding:"10px 20px" }}>
                <Mail size={13} /> Send Email
              </a>
            </div>
          </div>

          {/* Location — c4 */}
          <div className={`card ct-scl d3${vis?" in":""} c4`}>
            <div className="card-body" style={{ display:"flex",flexDirection:"column",gap:18 }}>
              <div className="ibox lg"><MapPin size={22} color={R} strokeWidth={1.6} /></div>
              <div>
                <div style={{ fontSize:11,fontWeight:700,letterSpacing:"1.8px",textTransform:"uppercase",color:R,marginBottom:6 }}>Location</div>
                <div style={{ fontSize:"clamp(18px,2vw,24px)",fontWeight:900,color:"#fff",letterSpacing:-.3,marginBottom:8 }}>Nairobi, Kenya</div>
                <p style={{ fontSize:13.5,color:MT,lineHeight:1.65 }}>Serving clients nationwide with delivery across all of Kenya.</p>
              </div>
              <a href="https://maps.google.com/?q=Nairobi+Kenya" target="_blank" rel="noopener noreferrer" className="btn-g" style={{ alignSelf:"flex-start",fontSize:13,padding:"10px 20px" }}>
                <ExternalLink size={13} /> Get Directions
              </a>
            </div>
          </div>

          {/* Hours — c4 */}
          <div className={`card ct-scl d4${vis?" in":""} c4`}>
            <div className="card-body">
              <div style={{ display:"flex",alignItems:"center",gap:11,marginBottom:18 }}>
                <div className="ibox"><Clock size={18} color={R} strokeWidth={1.6} /></div>
                <div style={{ fontSize:11,fontWeight:700,letterSpacing:"1.8px",textTransform:"uppercase",color:R }}>Business Hours</div>
              </div>
              {[
                { day:"Monday – Friday", time:"8:00 AM – 6:00 PM", closed:false },
                { day:"Saturday",        time:"9:00 AM – 4:00 PM", closed:false },
                { day:"Sunday",          time:"Closed",             closed:true  },
              ].map(h => (
                <div className="h-row" key={h.day}>
                  <span className="h-day">{h.day}</span>
                  <span className={h.closed?"h-closed":"h-time"}>{h.time}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Stat mini — c2 each × 2 */}
          {[
            { n:"24h", l:"Response Time", Icon:Zap    },
            { n:"98%", l:"Satisfaction",  Icon:Star   },
            { n:"100+",l:"Projects Done", Icon:Award  },
            { n:"5+",  l:"Years Exp.",    Icon:Users  },
          ].map((s,i) => (
            <div key={s.l} className={`card ct-scl d${i+1}${vis?" in":""} c3`} style={{ padding:"clamp(16px,2vw,22px)",display:"flex",flexDirection:"column",justifyContent:"space-between",minHeight:130 }}>
              <s.Icon size={18} color={R} strokeWidth={1.5} />
              <div>
                <div style={{ fontSize:"clamp(20px,2.8vw,34px)",fontWeight:900,color:"#fff",lineHeight:1,letterSpacing:-1 }}>{s.n}</div>
                <div style={{ fontSize:11.5,color:MT,marginTop:4,fontWeight:500 }}>{s.l}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── CONTACT FORM ──────────────────────────────────────────────
function ContactForm() {
  const [ref, vis] = useInView(0.06);
  const [form, setForm] = useState({ name:"",company:"",phone:"",email:"",service:"",budget:"",message:"",consult:false });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim())    e.name    = "Name is required";
    if (!form.phone.trim())   e.phone   = "Phone is required";
    if (!form.email.trim())   e.email   = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = "Enter a valid email";
    if (!form.service)        e.service = "Please select a service";
    if (!form.message.trim()) e.message = "Please describe your project";
    return e;
  };

  const submit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setStatus("sending");
    setTimeout(() => setStatus("success"), 1800);
  };

  const reset = () => {
    setStatus("idle");
    setForm({ name:"",company:"",phone:"",email:"",service:"",budget:"",message:"",consult:false });
  };

  return (
    <section className="sec" ref={ref} aria-label="Contact form">
      <div className="sec-inner">
        <div
          className="ct-form-grid"
          style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:"clamp(32px,5vw,72px)",alignItems:"start" }}
        >
          {/* LEFT — form */}
          <div className={`ct-left${vis?" in":""}`}>
            <div style={{ marginBottom:14 }}><Pill>Send a Message</Pill></div>
            <h2 className="sec-h2" style={{ marginBottom:10 }}>Tell Us About<br /><em>Your Project</em></h2>
            <p style={{ fontSize:"clamp(14px,1.35vw,16px)",color:MT,lineHeight:1.74,marginBottom:32,maxWidth:460 }}>
              Fill out the form and our team will get back to you within 24 hours with a detailed response.
            </p>

            <div className="form-wrap">
              {status === "success" ? (
                <div className="success-wrap">
                  <div className="success-ring"><CheckCircle size={30} color={R} /></div>
                  <div style={{ fontSize:20,fontWeight:800,color:"#fff" }}>Message Sent!</div>
                  <p style={{ fontSize:14,color:MT,lineHeight:1.72,maxWidth:320 }}>
                    Thank you for reaching out. Our team will respond within 24 hours.
                  </p>
                  <button className="btn-r" style={{ fontSize:13,padding:"10px 22px" }} onClick={reset}>
                    Send Another <ArrowRight size={13} />
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} noValidate>
                  <div style={{ display:"flex",flexDirection:"column",gap:14 }}>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label" htmlFor="f-name">Full Name *</label>
                        <input id="f-name" className={`form-input${errors.name?" err":""}`} type="text" placeholder="John Kamau" value={form.name} onChange={e=>set("name",e.target.value)} />
                        {errors.name && <span className="err-msg">{errors.name}</span>}
                      </div>
                      <div className="form-group">
                        <label className="form-label" htmlFor="f-co">Company Name</label>
                        <input id="f-co" className="form-input" type="text" placeholder="Savanna Ltd (optional)" value={form.company} onChange={e=>set("company",e.target.value)} />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label" htmlFor="f-ph">Phone Number *</label>
                        <input id="f-ph" className={`form-input${errors.phone?" err":""}`} type="tel" placeholder="0700 000 000" value={form.phone} onChange={e=>set("phone",e.target.value)} />
                        {errors.phone && <span className="err-msg">{errors.phone}</span>}
                      </div>
                      <div className="form-group">
                        <label className="form-label" htmlFor="f-em">Email Address *</label>
                        <input id="f-em" className={`form-input${errors.email?" err":""}`} type="email" placeholder="you@company.com" value={form.email} onChange={e=>set("email",e.target.value)} />
                        {errors.email && <span className="err-msg">{errors.email}</span>}
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label" htmlFor="f-svc">Service Needed *</label>
                        <select id="f-svc" className={`form-input form-select${errors.service?" err":""}`} value={form.service} onChange={e=>set("service",e.target.value)}>
                          <option value="">Select a service...</option>
                          {SERVICES_OPTS.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                        {errors.service && <span className="err-msg">{errors.service}</span>}
                      </div>
                      <div className="form-group">
                        <label className="form-label" htmlFor="f-bgt">Project Budget</label>
                        <select id="f-bgt" className="form-input form-select" value={form.budget} onChange={e=>set("budget",e.target.value)}>
                          <option value="">Select budget range...</option>
                          {BUDGETS.map(b => <option key={b} value={b}>{b}</option>)}
                        </select>
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label" htmlFor="f-msg">Project Details *</label>
                      <textarea id="f-msg" className={`form-input${errors.message?" err":""}`} rows={5} placeholder="Tell us about your project, timeline and requirements..." value={form.message} onChange={e=>set("message",e.target.value)} style={{ minHeight:120 }} />
                      {errors.message && <span className="err-msg">{errors.message}</span>}
                    </div>

                    <label className="check-lbl">
                      <input className="check-inp" type="checkbox" checked={form.consult} onChange={e=>set("consult",e.target.checked)} />
                      Request a free 30-minute consultation call before the project starts
                    </label>

                    <button type="submit" className="btn-r full" disabled={status==="sending"} style={{ marginTop:4,opacity:status==="sending"?.72:1 }}>
                      {status==="sending" ? "Sending..." : <><Send size={15} /> Send Inquiry</>}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* RIGHT — support panel */}
          <div className={`ct-right${vis?" in":""}`} style={{ display:"flex",flexDirection:"column",gap:14 }}>
            {/* Why us */}
            <div style={{ background:"linear-gradient(135deg,#1a0404 0%,#1c1c1c 100%)",border:`1px solid rgba(227,30,36,.2)`,borderRadius:18,padding:"clamp(24px,3vw,36px)",position:"relative",overflow:"hidden" }}>
              <div style={{ position:"absolute",inset:0,background:"radial-gradient(circle at 70% 30%,rgba(227,30,36,.12) 0%,transparent 60%)",pointerEvents:"none" }} />
              <div style={{ position:"relative",zIndex:1 }}>
                <div style={{ marginBottom:18 }}><Pill>Why Choose Us</Pill></div>
                <h3 style={{ fontSize:"clamp(17px,2vw,23px)",fontWeight:900,color:"#fff",lineHeight:1.1,letterSpacing:-.5,marginBottom:20 }}>You're in good hands</h3>
                {[
                  "Response Within 24 Hours",
                  "Free Consultation Available",
                  "Professional Dedicated Support",
                  "Transparent Pricing — No Hidden Fees",
                  "End-to-End Project Management",
                  "Nationwide Delivery Across Kenya",
                ].map(text => (
                  <div key={text} style={{ display:"flex",alignItems:"center",gap:10,marginBottom:11 }}>
                    <div style={{ width:20,height:20,borderRadius:5,background:"rgba(227,30,36,.18)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                      <CheckCircle size={12} color={R} />
                    </div>
                    <span style={{ fontSize:13.5,color:MTL,fontWeight:500 }}>{text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Stat mini grid */}
            <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10 }}>
              {[
                { n:"100+",l:"Projects Done",  Icon:Award },
                { n:"5+",  l:"Years Exp.",     Icon:Star  },
                { n:"24h", l:"Response Time",  Icon:Zap   },
                { n:"98%", l:"Happy Clients",  Icon:Users },
              ].map(s => (
                <div key={s.l} style={{ background:S1,border:`1px solid ${BD}`,borderRadius:12,padding:"clamp(14px,1.8vw,20px)",display:"flex",flexDirection:"column",gap:10 }}>
                  <s.Icon size={18} color={R} strokeWidth={1.5} />
                  <div>
                    <div style={{ fontSize:"clamp(18px,2.5vw,28px)",fontWeight:900,color:"#fff",lineHeight:1,letterSpacing:-1 }}>{s.n}</div>
                    <div style={{ fontSize:11.5,color:MT,marginTop:3,fontWeight:500 }}>{s.l}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Direct contact */}
            <div style={{ background:S1,border:`1px solid ${BD}`,borderRadius:14,padding:"clamp(16px,2vw,22px)" }}>
              <div style={{ fontSize:11,fontWeight:700,letterSpacing:"1.6px",textTransform:"uppercase",color:R,marginBottom:12 }}>
                Prefer Direct Contact?
              </div>
              {[
                { Icon:Phone,  val:"0794 437 498",              href:"tel:0794437498" },
                { Icon:Mail,   val:"info@bestmarkdesigner.com", href:"mailto:info@bestmarkdesigner.com" },
                { Icon:MapPin, val:"Nairobi, Kenya",            href:"#" },
              ].map(c => (
                <a key={c.val} href={c.href} style={{ display:"flex",alignItems:"center",gap:10,padding:"9px 0",borderBottom:`1px solid ${BD}`,color:MTL,fontSize:13,fontWeight:500,transition:"color .2s",textDecoration:"none" }}
                  onMouseEnter={e=>e.currentTarget.style.color="#fff"}
                  onMouseLeave={e=>e.currentTarget.style.color=MTL}
                >
                  <c.Icon size={14} color={R} />{c.val}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── QUICK SERVICES ────────────────────────────────────────────
function QuickServices() {
  const [ref, vis] = useInView(0.06);
  return (
    <section className="sec" ref={ref} aria-label="Quick services" style={{ background:S1,borderTop:`1px solid ${BD}`,borderBottom:`1px solid ${BD}` }}>
      <div className="sec-inner">
        <div style={{ textAlign:"center",marginBottom:44 }}>
          <div className={`ct-fade${vis?" in":""}`} style={{ display:"flex",justifyContent:"center",marginBottom:14 }}><Pill>Our Services</Pill></div>
          <h2 className={`ct-fade d1${vis?" in":""} sec-h2`} style={{ textAlign:"center" }}>What Can We Help You With?</h2>
        </div>
        <div className="svc-bento" style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10 }}>
          {QUICK_SVCS.map(({ Icon, label, desc }, i) => (
            <div key={label} className={`card ct-scl d${i+1}${vis?" in":""}`} style={{ padding:"clamp(20px,2.5vw,30px)",minHeight:160,display:"flex",flexDirection:"column",gap:14 }}>
              <div style={{ position:"absolute",inset:0,background:"radial-gradient(circle at 80% 16%,rgba(227,30,36,.06) 0%,transparent 55%)",pointerEvents:"none" }} />
              <div style={{ position:"relative",zIndex:1,display:"flex",flexDirection:"column",gap:12 }}>
                <div className="ibox"><Icon size={18} color={R} strokeWidth={1.6} /></div>
                <div>
                  <div style={{ fontSize:10,fontWeight:700,letterSpacing:"1.8px",textTransform:"uppercase",color:R,marginBottom:5 }}>Service</div>
                  <div style={{ fontSize:"clamp(14px,1.5vw,17px)",fontWeight:800,color:"#fff",letterSpacing:-.3,marginBottom:6 }}>{label}</div>
                  <p style={{ fontSize:13,color:MT,lineHeight:1.65 }}>{desc}</p>
                </div>
              </div>
              <button className="btn-g sm" style={{ position:"relative",zIndex:1,alignSelf:"flex-start" }}>
                Learn More <ChevronRight size={11} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── STATS STRIP ───────────────────────────────────────────────
function Stats() {
  const [ref, vis] = useInView(0.1);
  return (
    <div ref={ref} style={{ background:BG,borderBottom:`1px solid ${BD}` }}>
      <div style={{ maxWidth:1240,margin:"0 auto" }}>
        <div className="stats-bar">
          {[
            { n:"100", sx:"+", l:"Projects Completed" },
            { n:"98",  sx:"%", l:"Client Satisfaction" },
            { n:"24",  sx:"hr",l:"Response Time" },
            { n:"5",   sx:"+", l:"Years Experience" },
          ].map((s,i) => (
            <div key={s.l} className={`stat-item ct-fade d${i+1}${vis?" in":""}`}>
              <div className="stat-n"><span>{s.n}</span>{s.sx}</div>
              <div className="stat-l">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── FAQ ───────────────────────────────────────────────────────
function FAQ() {
  const [ref, vis] = useInView(0.06);
  const [open, setOpen] = useState(null);
  const refs = useRef([]);

  const toggle = (i) => setOpen(prev => prev === i ? null : i);

  return (
    <section className="sec" ref={ref} aria-label="FAQ">
      <div className="sec-inner">
        <div
          className="ct-faq-grid"
          style={{ display:"grid",gridTemplateColumns:"1fr 1.4fr",gap:"clamp(32px,5vw,72px)",alignItems:"start" }}
        >
          <div className={`ct-left${vis?" in":""}`}>
            <div style={{ marginBottom:14 }}><Pill>FAQ</Pill></div>
            <h2 className="sec-h2" style={{ marginBottom:14 }}>Frequently Asked<br /><em>Questions</em></h2>
            <p style={{ fontSize:"clamp(14px,1.35vw,16px)",color:MT,lineHeight:1.74,marginBottom:28 }}>
              Can't find what you're looking for? Call us or send a message and we'll help you out.
            </p>
            <a href="tel:0794437498" className="btn-r" style={{ fontSize:13,padding:"11px 22px" }}>
              <Phone size={13} /> Call Us Directly
            </a>
          </div>

          <div className={`ct-right${vis?" in":""}`}>
            {FAQS.map((faq, i) => (
              <div key={i} className="faq-item">
                <button
                  className="faq-btn"
                  onClick={() => toggle(i)}
                  aria-expanded={open === i}
                  aria-controls={`faq-${i}`}
                >
                  {faq.q}
                  <ChevronDown className={`faq-icon${open===i?" open":""}`} size={18} />
                </button>
                <div
                  id={`faq-${i}`}
                  className={`faq-body${open===i?" open":" closed"}`}
                  style={{ maxHeight: open===i ? "220px" : "0" }}
                  aria-hidden={open !== i}
                >
                  <p className="faq-text">{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── MAP ───────────────────────────────────────────────────────
function MapSection() {
  const [ref, vis] = useInView(0.08);
  return (
    <section className="sec" ref={ref} aria-label="Location" style={{ background:S1,borderTop:`1px solid ${BD}` }}>
      <div className="sec-inner">
        <div style={{ textAlign:"center",marginBottom:36 }}>
          <div className={`ct-fade${vis?" in":""}`} style={{ display:"flex",justifyContent:"center",marginBottom:14 }}><Pill>Find Us</Pill></div>
          <h2 className={`ct-fade d1${vis?" in":""} sec-h2`} style={{ textAlign:"center" }}>Our Location in <em>Nairobi</em></h2>
        </div>
        <div className={`ct-scl${vis?" in":""} map-shell`}>
          <iframe
            title="Bestmark Designers — Nairobi, Kenya"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d255282.3587940254!2d36.68258863563985!3d-1.3028617959783487!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x182f1172d84d49a7%3A0xf7cf0254b297924c!2sNairobi%2C%20Kenya!5e0!3m2!1sen!2ske!4v1710000000000!5m2!1sen!2ske"
            width="100%"
            height="440"
            style={{ border:0,display:"block",filter:"invert(90%) hue-rotate(180deg) saturate(.8) brightness(.88)" }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
          <div className="map-overlay">
            <div style={{ fontSize:10,fontWeight:700,letterSpacing:"1.4px",textTransform:"uppercase",color:R,marginBottom:5 }}>Our Location</div>
            <div style={{ fontSize:14,fontWeight:800,color:"#fff",marginBottom:3 }}>Bestmark Designers</div>
            <div style={{ fontSize:12.5,color:MT,marginBottom:12 }}>Nairobi, Kenya</div>
            <a href="https://maps.google.com/?q=Nairobi+Kenya" target="_blank" rel="noopener noreferrer" className="btn-r" style={{ fontSize:11.5,padding:"7px 14px" }}>
              <ExternalLink size={11} /> Get Directions
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── SOCIAL ────────────────────────────────────────────────────
function SocialSection() {
  const [ref, vis] = useInView(0.08);
  return (
    <section className="sec" ref={ref} aria-label="Social media">
      <div className="sec-inner">
        <div style={{ textAlign:"center",marginBottom:44 }}>
          <div className={`ct-fade${vis?" in":""}`} style={{ display:"flex",justifyContent:"center",marginBottom:14 }}><Pill>Stay Connected</Pill></div>
          <h2 className={`ct-fade d1${vis?" in":""} sec-h2`} style={{ textAlign:"center" }}>Connect With <em>Us Online</em></h2>
          <p className={`ct-fade d2${vis?" in":""}`} style={{ fontSize:"clamp(14px,1.35vw,16px)",color:MT,lineHeight:1.74,maxWidth:440,margin:"12px auto 0" }}>
            Follow us for design inspiration, behind-the-scenes content and the latest from our studio.
          </p>
        </div>
        <div className="soc-grid" style={{ display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:12 }}>
          {SOCIALS.map(({ Icon, label, color, bg, href }, i) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className={`soc-btn ct-scl d${i+1}${vis?" in":""}`}
              aria-label={`Follow on ${label}`}
              style={{ "--soc-color": color }}
              onMouseEnter={e => { e.currentTarget.style.background=bg; e.currentTarget.style.borderColor=`${color}44`; }}
              onMouseLeave={e => { e.currentTarget.style.background="rgba(255,255,255,.03)"; e.currentTarget.style.borderColor="rgba(255,255,255,.07)"; }}
            >
              <div className="soc-icon-wrap" style={{ background:bg,border:`1px solid ${color}44` }}>
                <Icon size={22} color={color} strokeWidth={1.6} />
              </div>
              <span className="soc-label">{label}</span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Root ──────────────────────────────────────────────────────
export default function ContactPage() {
  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: CSS }} />
      <a href="#ct-main" style={{ position:"absolute",left:-9999,top:0,zIndex:9999,background:R,color:"#fff",padding:"8px 16px",fontWeight:700,fontSize:14 }}>
        Skip to main content
      </a>
      <main id="ct-main">
        <Hero />
        <GD />
        <ContactInfo />
        <GD />
        <ContactForm />
        <GD />
        <QuickServices />
        <Stats />
        <GD />
        <FAQ />
        <GD />
        <MapSection />
        <GD />
        <SocialSection />
      </main>
      
    </>
  );
}